import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DOC_KINDS, DOC_KIND_MAP, type DocKind } from '@goyone/shared';
import {
  Badge,
  EmptyState,
  ErrorState,
  Modal,
  Notice,
  PageHead,
  Stat,
} from '../../components/ui';
import { Icon } from '../../components/Icon';
import { useToast } from '../../components/Toast';
import { api, qs } from '../../lib/api';
import { date, num, plural, WORDS } from '../../lib/format';
import { useAuth } from '../../auth/AuthProvider';

/* ==========================================================================
   الصفوف كما يرسلها الخادم
   ========================================================================== */

export interface DocRow {
  id: string;
  kind: DocKind;
  kindLabel: string;
  kindKind: string;
  title: string;
  label: string | null;
  number: string | null;
  issuedOn: string | null;
  expiresOn: string | null;
  state: 'VALID' | 'DUE' | 'EXPIRED' | 'FOREVER';
  stateLabel: string;
  stateKind: string;
  daysLeft: number | null;
  attachmentId: string | null;
  verifiedAt: string | null;
  notes: string | null;
  employee: { id: string; name: string; code: string | null; status: string };
}

interface Summary {
  documents: number;
  onDuty: number;
  missing: number;
  due: number;
  expired: number;
  required: number;
  warnDays: number;
}

interface Gaps {
  required: { kind: string; label: string }[];
  rows: {
    employee: {
      id: string;
      name: string;
      code: string | null;
      department: string | null;
    };
    missing: { kind: string; label: string }[];
  }[];
}

interface Required {
  kinds: DocKind[];
  catalog: {
    key: DocKind;
    label: string;
    expires: boolean;
    numbered: boolean;
  }[];
  warnDays: number;
}

type Tab = 'due' | 'gaps' | 'required';

const TABS: readonly { key: Tab; label: string; adminOnly?: boolean }[] = [
  { key: 'due', label: 'ما ينتهي قريبًا' },
  { key: 'gaps', label: 'ملفّاتٌ ناقصة' },
  { key: 'required', label: 'ما تطلبه الشركة', adminOnly: true },
] as const;

/* ==========================================================================
   الصفحة
   ========================================================================== */

/**
 * مستندات الموظّفين.
 *
 * ## ولماذا بابٌ فوق المرفقات
 *
 * النظامُ يرفع الملفّاتِ بالفعل، لكنّ مجلّدًا لا يُجيب سؤالين تُسأل بهما
 * إدارةُ الموارد البشرية كلَّ شهر: مَن ينقصه مستندٌ مطلوب، ومَن ينتهي
 * مستندُه قريبًا. والنوعُ وتاريخُ الانتهاء هما ما يُنتجهما.
 *
 * ## وما لا يقع هنا
 *
 * لا يتحقّق النظامُ من صحّة ورقةٍ ولا يقرأ ما فيها. «رُوجعت» واقعةٌ
 * يسجّلها من راجعها بعينه — وادّعاءُ غير ذلك يجعل ورقةً مزوَّرةً تبدو
 * معتمَدةً من النظام.
 */
export function Documents() {
  const { atLeast } = useAuth();
  const canAdmin = atLeast('ADMIN');
  const [tab, setTab] = useState<Tab>('due');

  const summary = useQuery({
    queryKey: ['documents', 'summary'],
    queryFn: () => api<Summary>('/hr/documents/summary'),
    enabled: canAdmin,
  });

  const s = summary.data;
  const tabs = TABS.filter((t) => !t.adminOnly || canAdmin);

  return (
    <div className="page">
      <PageHead
        title="مستندات الموظّفين"
        subtitle="ما أُودع من أوراق، ومن ينقصه مطلوب، وما ينتهي قريبًا"
      />

      {summary.isError && (
        <ErrorState
          error={summary.error}
          onRetry={() => void summary.refetch()}
        />
      )}

      {s && (
        <div className="stats">
          <Stat
            label="أوراقٌ مودَعة"
            value={num(s.documents)}
            note={`لـ${num(s.onDuty)} على رأس العمل`}
          />
          <Stat
            label="ملفّاتٌ ناقصة"
            value={num(s.missing)}
            note={`من ${num(s.required)} ورقةً تطلبها الشركة`}
          />
          <Stat
            label="تنتهي قريبًا"
            value={num(s.due)}
            note={`خلال ${num(s.warnDays)} يومًا`}
          />
          <Stat
            label="منتهية"
            value={num(s.expired)}
            note={
              s.expired > 0
                ? 'ومن يعمل بورقةٍ منتهيةٍ يعمل بلا ورقة'
                : 'لا ورقةَ منتهية'
            }
          />
        </div>
      )}

      {s && s.expired > 0 && (
        <Notice title="أوراقٌ انتهت" icon="alert" kind="danger">
          {plural(s.expired, WORDS.record)} مضى تاريخُ انتهائها. وتصريحُ عملٍ
          منتهٍ يوقف صاحبَه قانونًا لا في النظام وحدَه.
        </Notice>
      )}

      <div className="tabs" role="tablist" aria-label="أقسام المستندات">
        {tabs.map((t) => (
          <button
            key={t.key}
            className="tab"
            role="tab"
            aria-selected={tab === t.key}
            onClick={() => setTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'due' && <DueSection />}
      {tab === 'gaps' && <GapsSection />}
      {tab === 'required' && canAdmin && <RequiredSection />}
    </div>
  );
}

/* ==========================================================================
   ما ينتهي قريبًا
   ========================================================================== */

function DueSection() {
  const list = useQuery({
    queryKey: ['documents', 'due'],
    queryFn: () => api<DocRow[]>(`/hr/documents${qs({ due: '1' })}`),
  });

  const rows = list.data ?? [];

  return (
    <div className="card mt-3">
      <h2 className="card-title">ما ينتهي قريبًا أو انتهى</h2>

      {list.isError && (
        <ErrorState error={list.error} onRetry={() => void list.refetch()} />
      )}
      {list.isPending && <div className="skeleton" style={{ height: 160 }} />}

      {list.data && rows.length === 0 && (
        <EmptyState
          title="لا شيءَ ينتهي قريبًا"
          text="الأوراقُ التي لها تاريخُ انتهاءٍ كلُّها سارية."
        />
      )}

      {rows.length > 0 && (
        <div className="table-wrap sticky-head mt-3">
          <table className="table">
            <thead>
              <tr>
                <th>الموظّف</th>
                <th>الورقة</th>
                <th>الرقم</th>
                <th>تنتهي في</th>
                <th>الباقي</th>
                <th>الحالة</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((d) => (
                <tr key={d.id}>
                  <td>
                    <Link
                      className="link"
                      to={`/hr/employees/${d.employee.id}`}
                    >
                      {d.employee.name}
                    </Link>
                  </td>
                  <td>{d.title}</td>
                  <td className="tiny">{d.number ?? '—'}</td>
                  <td className="tiny">
                    {d.expiresOn ? date(d.expiresOn) : '—'}
                  </td>
                  <td className="num">
                    {d.daysLeft === null
                      ? '—'
                      : d.daysLeft >= 0
                        ? `${num(d.daysLeft)} يومًا`
                        : `مضى ${num(-d.daysLeft)} يومًا`}
                  </td>
                  <td>
                    <Badge kind={d.stateKind}>{d.stateLabel}</Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ==========================================================================
   ملفّاتٌ ناقصة
   ========================================================================== */

function GapsSection() {
  const gaps = useQuery({
    queryKey: ['documents', 'gaps'],
    queryFn: () => api<Gaps>('/hr/documents/gaps'),
  });

  const d = gaps.data;

  return (
    <div className="card mt-3">
      <h2 className="card-title">من ينقصه مستندٌ مطلوب</h2>

      {/*
        الناقصُ يُقال بأسمائه لا برقم: «ينقصه مستندان» رسالةٌ يقف أمامها
        من يريد أن يطلبهما منه اليوم.
      */}
      {d && d.required.length === 0 && (
        <Notice title="لا ورقةَ مطلوبة" icon="info">
          لم تحدّد الشركةُ ما تطلبه من أوراق — فلا ملفَّ ناقصًا بالتعريف.
        </Notice>
      )}

      {gaps.isError && (
        <ErrorState error={gaps.error} onRetry={() => void gaps.refetch()} />
      )}
      {gaps.isPending && <div className="skeleton" style={{ height: 160 }} />}

      {d && d.required.length > 0 && d.rows.length === 0 && (
        <EmptyState
          title="لا ملفَّ ناقصًا"
          text={`كلُّ من على رأس العمل أودع ${d.required.map((r) => r.label).join('، ')}.`}
        />
      )}

      {d && d.rows.length > 0 && (
        <>
          <p className="tiny mute mt-2">
            المطلوب: {d.required.map((r) => r.label).join('، ')}
          </p>
          <div className="table-wrap sticky-head mt-2">
            <table className="table">
              <thead>
                <tr>
                  <th>الموظّف</th>
                  <th>القسم</th>
                  <th>ما ينقصه</th>
                </tr>
              </thead>
              <tbody>
                {d.rows.map((r) => (
                  <tr key={r.employee.id}>
                    <td>
                      <Link
                        className="link"
                        to={`/hr/employees/${r.employee.id}`}
                      >
                        {r.employee.name}
                      </Link>
                    </td>
                    <td className="tiny">{r.employee.department ?? '—'}</td>
                    <td className="tiny">
                      {r.missing.map((m) => m.label).join('، ')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

/* ==========================================================================
   ما تطلبه الشركة
   ========================================================================== */

function RequiredSection() {
  const queryClient = useQueryClient();
  const toast = useToast();

  const required = useQuery({
    queryKey: ['documents', 'required'],
    queryFn: () => api<Required>('/hr/documents/required'),
  });

  const [picked, setPicked] = useState<DocKind[] | null>(null);
  const kinds = picked ?? required.data?.kinds ?? [];

  const save = useMutation({
    mutationFn: () =>
      api('/hr/documents/required', {
        method: 'PATCH',
        body: { kinds },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['documents'] });
      setPicked(null);
      toast('حُفظ ما تطلبه الشركة.');
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّر الحفظ'),
  });

  const toggle = (k: DocKind) =>
    setPicked(kinds.includes(k) ? kinds.filter((x) => x !== k) : [...kinds, k]);

  return (
    <div className="card mt-3">
      <div className="row" style={{ gap: 'var(--sp-2)' }}>
        <h2 className="card-title" style={{ flex: 1 }}>
          ما تطلبه الشركة من أوراق
        </h2>
        <button
          className="btn btn-primary"
          disabled={save.isPending || picked === null}
          onClick={() => save.mutate()}
        >
          احفظ
        </button>
      </div>

      {/*
        إعدادُ شركةٍ لا ثابتٌ في الشيفرة: ما يلزم في مصرَ غيرُ ما يلزم في
        الخليج، وفرضُ قائمةٍ واحدةٍ يجعل «ملفّاتٌ ناقصة» رقمًا لا يصف أحدًا.
      */}
      <Notice title="ولماذا تُختار" icon="info">
        ما يلزم في مصرَ غيرُ ما يلزم في الخليج. وهذه القائمةُ هي وحدَها ما يُقاس
        عليه «ملفٌّ ناقص» — ومن تركها فارغةً لا ملفَّ ناقصًا عنده بالتعريف.
      </Notice>

      {required.isPending && (
        <div className="skeleton" style={{ height: 160 }} />
      )}

      {required.data && (
        <div className="table-wrap mt-3">
          <table className="table">
            <thead>
              <tr>
                <th />
                <th>الورقة</th>
                <th>لها تاريخُ انتهاء</th>
                <th>لها رقم</th>
              </tr>
            </thead>
            <tbody>
              {required.data.catalog
                .filter((c) => c.key !== 'OTHER')
                .map((c) => (
                  <tr key={c.key}>
                    <td style={{ width: 40 }}>
                      <input
                        type="checkbox"
                        aria-label={`اطلب ${c.label}`}
                        checked={kinds.includes(c.key)}
                        onChange={() => toggle(c.key)}
                      />
                    </td>
                    <td>{c.label}</td>
                    <td className="tiny mute">{c.expires ? 'نعم' : '—'}</td>
                    <td className="tiny mute">{c.numbered ? 'نعم' : '—'}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="tiny mute mt-2">
        و«أخرى» لا تُطلب: هي لما لا يقع في الأنواع أعلاه، ولا يُقاس عليها نقصٌ
        لأنّ عنوانَها يُكتب في كلّ مرّة.
      </p>
    </div>
  );
}

/* ==========================================================================
   قسمُ ملفّ الموظّف
   ========================================================================== */

interface EmployeeDocs {
  employee: { id: string; name: string };
  documents: DocRow[];
  required: DocKind[];
  missing: { kind: string; label: string }[];
  warnDays: number;
}

/**
 * أوراقُ موظّفٍ بعينه — تُعرض في ملفّه.
 *
 * والناقصُ يُقال بأسمائه فوق الجدول: من يفتح الملفَّ يريد أن يعرف ما
 * يطلبه منه اليوم، لا أن يقارن جدولًا بقائمةٍ في شاشةٍ أخرى.
 */
export function EmployeeDocumentsCard({ employeeId }: { employeeId: string }) {
  const { atLeast } = useAuth();
  const canRead = atLeast('ADMIN');
  const [writing, setWriting] = useState(false);
  const [editing, setEditing] = useState<DocRow | null>(null);

  const queryClient = useQueryClient();
  const toast = useToast();

  const docs = useQuery({
    queryKey: ['documents', 'employee', employeeId],
    queryFn: () => api<EmployeeDocs>(`/hr/documents/employees/${employeeId}`),
    enabled: canRead,
  });

  const verify = useMutation({
    mutationFn: (id: string) =>
      api(`/hr/documents/${id}/verify`, { method: 'POST' }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['documents'] });
      toast('سُجّلت المراجعة.');
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّر الحفظ'),
  });

  const remove = useMutation({
    mutationFn: (id: string) =>
      api(`/hr/documents/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['documents'] });
      toast('حُذف السطر — والمرفقُ يبقى في بابه.');
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّر الحذف'),
  });

  if (!canRead) return null;

  const d = docs.data;

  return (
    <div className="card mt-4">
      <div className="card-head">
        <h2 className="card-title">الأوراق</h2>
        <div className="row" style={{ gap: 6 }}>
          <Link className="link tiny" to="/hr/documents">
            كلُّ المستندات
          </Link>
          <button className="btn btn-sm" onClick={() => setWriting(true)}>
            <Icon name="plus" size={13} />
            ورقة
          </button>
        </div>
      </div>

      {docs.isPending && <div className="skeleton" style={{ height: 80 }} />}

      {d && d.missing.length > 0 && (
        <Notice title="ينقصه" icon="alert" kind="warning">
          {d.missing.map((m) => m.label).join('، ')}.
        </Notice>
      )}

      {d && d.documents.length === 0 && (
        <p className="tiny mute">لا أوراقَ مودَعة.</p>
      )}

      {d && d.documents.length > 0 && (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>الورقة</th>
                <th>الرقم</th>
                <th>تنتهي</th>
                <th>الحالة</th>
                <th>رُوجعت</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {d.documents.map((x) => (
                <tr key={x.id}>
                  <td>{x.title}</td>
                  <td className="cell-mute">{x.number ?? '—'}</td>
                  <td className="num cell-mute">
                    {x.expiresOn ? date(x.expiresOn) : '—'}
                  </td>
                  <td>
                    <Badge kind={x.stateKind}>{x.stateLabel}</Badge>
                  </td>
                  <td className="num cell-mute">
                    {x.verifiedAt ? date(x.verifiedAt) : '—'}
                  </td>
                  <td>
                    <div className="row" style={{ gap: 4 }}>
                      <button
                        className="btn btn-sm"
                        onClick={() => setEditing(x)}
                      >
                        تعديل
                      </button>
                      {!x.verifiedAt && (
                        <button
                          className="btn btn-sm"
                          disabled={verify.isPending}
                          onClick={() => verify.mutate(x.id)}
                        >
                          راجعتُها
                        </button>
                      )}
                      <button
                        className="btn btn-sm btn-ghost"
                        aria-label="احذف السطر"
                        disabled={remove.isPending}
                        onClick={() => remove.mutate(x.id)}
                      >
                        <Icon name="trash" size={13} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/*
        «رُوجعت» واقعةٌ يسجّلها من راجعها بعينه: النظامُ لا يقرأ ورقةً ولا
        يتحقّق منها، وادّعاءُ غير ذلك يجعل ورقةً مزوَّرةً تبدو معتمَدة.
      */}
      <p className="tiny mute mt-2">
        «رُوجعت» تعني أنّ من ضغطها نظر في الورقة وأثبت مطابقتَها — لا أنّ
        النظامَ تحقّق منها. وصورةُ الورقة تُرفع في «ملفات الموظف» أسفلَ الصفحة،
        ويُشار إليها هنا.
      </p>

      {(writing || editing) && (
        <DocDialog
          employeeId={employeeId}
          doc={editing}
          onClose={() => {
            setWriting(false);
            setEditing(null);
          }}
        />
      )}
    </div>
  );
}

function DocDialog({
  employeeId,
  doc,
  onClose,
}: {
  employeeId: string;
  doc: DocRow | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const toast = useToast();

  const [kind, setKind] = useState<DocKind>(doc?.kind ?? 'NATIONAL_ID');
  const [label, setLabel] = useState(doc?.label ?? '');
  const [number, setNumber] = useState(doc?.number ?? '');
  const [issuedOn, setIssuedOn] = useState(doc?.issuedOn ?? '');
  const [expiresOn, setExpiresOn] = useState(doc?.expiresOn ?? '');
  const [notes, setNotes] = useState(doc?.notes ?? '');

  const def = DOC_KIND_MAP[kind];

  const save = useMutation({
    mutationFn: () =>
      api(
        doc
          ? `/hr/documents/employees/${employeeId}/${doc.id}`
          : `/hr/documents/employees/${employeeId}`,
        {
          method: doc ? 'PATCH' : 'POST',
          body: {
            kind,
            label: label || null,
            number: number || null,
            issuedOn: issuedOn || null,
            expiresOn: expiresOn || null,
            notes: notes || null,
          },
        },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['documents'] });
      toast('حُفظت الورقة.');
      onClose();
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّر الحفظ'),
  });

  const ready = kind !== 'OTHER' || label.trim().length > 0;

  return (
    <Modal
      title={doc ? 'تعديل ورقة' : 'ورقة جديدة'}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            إلغاء
          </button>
          <button
            className="btn btn-primary"
            disabled={save.isPending || !ready}
            onClick={() => save.mutate()}
          >
            احفظ
          </button>
        </>
      }
    >
      <div className="form-grid">
        <div className="field span-2">
          <label className="label" htmlFor="ed-kind">
            النوع
          </label>
          <select
            id="ed-kind"
            className="select"
            value={kind}
            onChange={(e) => setKind(e.target.value as DocKind)}
          >
            {DOC_KINDS.map((k) => (
              <option key={k} value={k}>
                {DOC_KIND_MAP[k].label}
              </option>
            ))}
          </select>
          <span className="tiny mute">{def.description}</span>
        </div>

        {kind === 'OTHER' && (
          <div className="field span-2">
            <label className="label req" htmlFor="ed-label">
              عنوان الورقة
            </label>
            <input
              id="ed-label"
              className="input"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
            />
            <span className="tiny mute">«أخرى» وحدَها لا تُعرف بعد شهر.</span>
          </div>
        )}

        {def.numbered && (
          <div className="field">
            <label className="label" htmlFor="ed-number">
              الرقم
            </label>
            <input
              id="ed-number"
              className="input"
              value={number}
              onChange={(e) => setNumber(e.target.value)}
            />
          </div>
        )}

        <div className="field">
          <label className="label" htmlFor="ed-issued">
            تاريخ الإصدار
          </label>
          <input
            id="ed-issued"
            className="input"
            type="date"
            value={issuedOn}
            onChange={(e) => setIssuedOn(e.target.value)}
          />
        </div>

        <div className="field">
          <label className="label" htmlFor="ed-expires">
            تاريخ الانتهاء
          </label>
          <input
            id="ed-expires"
            className="input"
            type="date"
            value={expiresOn}
            onChange={(e) => setExpiresOn(e.target.value)}
          />
          <span className="tiny mute">
            {def.expires
              ? 'هذه الورقةُ تنتهي — واتركُه فارغًا يُخرجها من التنبيه.'
              : 'هذه الورقةُ لا تنتهي غالبًا.'}
          </span>
        </div>

        <div className="field span-2">
          <label className="label" htmlFor="ed-notes">
            ملاحظات
          </label>
          <textarea
            id="ed-notes"
            className="input"
            rows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </div>
      </div>
    </Modal>
  );
}
